import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../models/api-response.model';

/**
 * Comunicación plataforma ⇄ talleres:
 * - Novedades: el operador (SUPER_ADMIN) avisa de nuevas funciones y correcciones
 *   a todos los usuarios; cada quien las descarta en su campana.
 * - Buzón: los talleres mandan quejas, sugerencias o fallas; el operador responde.
 */
export type TipoNovedad = 'FEATURE' | 'FIX' | 'AVISO';
export type TipoBuzon = 'QUEJA' | 'SUGERENCIA' | 'FALLA';
export type EstadoBuzon = 'NUEVO' | 'EN_REVISION' | 'RESUELTO' | 'DESCARTADO';

export interface Novedad {
  id: string;
  titulo: string;
  descripcion: string;
  tipo: TipoNovedad;
  version?: string | null;
  activa: boolean;
  leida?: boolean;
  createdAt: string;
  publicado_por?: { id: string; nombre: string };
  /** Vacío = para todos los talleres; con ids, sólo la ven esos talleres. */
  tenant_ids?: string[];
}

export interface MensajeBuzon {
  id: string;
  tenant_id: string;
  taller_nombre?: string;
  usuario: { id: string; nombre: string; email?: string; grupo?: string };
  tipo: TipoBuzon;
  asunto: string;
  mensaje: string;
  modulo?: string | null;
  estado: EstadoBuzon;
  respuesta?: string | null;
  respuesta_leida: boolean;
  respondido_por?: { id: string; nombre: string };
  respondido_at?: string;
  createdAt: string;
}

export const TIPOS_NOVEDAD: Record<TipoNovedad, { etiqueta: string; icono: string; clase: string }> = {
  FEATURE: { etiqueta: 'Nueva función', icono: 'ri-sparkling-2-line', clase: 'badge-success' },
  FIX: { etiqueta: 'Corrección', icono: 'ri-tools-line', clase: 'badge-info' },
  AVISO: { etiqueta: 'Aviso', icono: 'ri-megaphone-line', clase: 'badge-warning' },
};

export const TIPOS_BUZON: Record<TipoBuzon, { etiqueta: string; icono: string }> = {
  SUGERENCIA: { etiqueta: 'Sugerencia', icono: 'ri-lightbulb-line' },
  QUEJA: { etiqueta: 'Queja', icono: 'ri-emotion-unhappy-line' },
  FALLA: { etiqueta: 'Falla del sistema', icono: 'ri-bug-line' },
};

export const ESTADOS_BUZON: Record<EstadoBuzon, { etiqueta: string; clase: string }> = {
  NUEVO: { etiqueta: 'Nuevo', clase: 'badge-error' },
  EN_REVISION: { etiqueta: 'En revisión', clase: 'badge-warning' },
  RESUELTO: { etiqueta: 'Resuelto', clase: 'badge-success' },
  DESCARTADO: { etiqueta: 'Descartado', clase: 'badge-ghost' },
};

@Injectable({ providedIn: 'root' })
export class ComunicacionService {
  private readonly http = inject(HttpClient);
  private readonly urlNovedades = `${environment.apiUrl}/novedades`;
  private readonly urlBuzon = `${environment.apiUrl}/buzon`;

  /** Novedades sin leer y respuestas del buzón sin leer: alimentan la campana. */
  novedadesSinLeer = signal<Novedad[]>([]);
  respuestasSinLeer = signal<MensajeBuzon[]>([]);
  /** Sólo SUPER_ADMIN: mensajes nuevos en la bandeja. */
  buzonNuevos = signal(0);

  // ─── Novedades ───
  listarNovedades(todas = false): Observable<Novedad[]> {
    const params: Record<string, string> = todas ? { todas: '1' } : {};
    return this.http.get<ApiResponse<{ items: Novedad[] }>>(this.urlNovedades, { params })
      .pipe(map(r => r.data?.items ?? []));
  }

  refrescarNovedades(): void {
    this.listarNovedades().subscribe({
      next: items => this.novedadesSinLeer.set(items.filter(n => !n.leida)),
      error: () => { },
    });
  }

  publicarNovedad(n: Pick<Novedad, 'titulo' | 'descripcion' | 'tipo' | 'version' | 'tenant_ids'>): Observable<Novedad> {
    return this.http.post<ApiResponse<Novedad>>(this.urlNovedades, n).pipe(map(r => r.data));
  }

  editarNovedad(id: string, cambios: Partial<Novedad>): Observable<Novedad> {
    return this.http.put<ApiResponse<Novedad>>(`${this.urlNovedades}/${id}`, cambios).pipe(map(r => r.data));
  }

  marcarLeidas(ids: string[]): Observable<unknown> {
    return this.http.post(`${this.urlNovedades}/leidas`, { ids }).pipe(
      tap(() => this.novedadesSinLeer.update(l => l.filter(n => !ids.includes(n.id)))),
    );
  }

  // ─── Buzón ───
  listarBuzon(filtros: { estado?: string; tipo?: string } = {}):
    Observable<{ items: MensajeBuzon[]; nuevos?: number; respuestas_sin_leer?: number }> {
    const params: Record<string, string> = {};
    if (filtros.estado) params['estado'] = filtros.estado;
    if (filtros.tipo) params['tipo'] = filtros.tipo;
    return this.http.get<ApiResponse<{ items: MensajeBuzon[]; nuevos?: number; respuestas_sin_leer?: number }>>(
      this.urlBuzon, { params }).pipe(map(r => r.data));
  }

  refrescarBuzon(esSuperAdmin: boolean): void {
    this.listarBuzon().subscribe({
      next: d => esSuperAdmin
        ? this.buzonNuevos.set(d.nuevos ?? 0)
        : this.respuestasSinLeer.set(d.items.filter(m => !m.respuesta_leida)),
      error: () => { },
    });
  }

  enviarBuzon(m: { tipo: TipoBuzon; asunto: string; mensaje: string; modulo?: string }): Observable<MensajeBuzon> {
    return this.http.post<ApiResponse<MensajeBuzon>>(this.urlBuzon, m).pipe(map(r => r.data));
  }

  actualizarBuzon(id: string, cambios: { estado?: EstadoBuzon; respuesta?: string; respuesta_leida?: boolean }):
    Observable<MensajeBuzon> {
    return this.http.put<ApiResponse<MensajeBuzon>>(`${this.urlBuzon}/${id}`, cambios).pipe(
      map(r => r.data),
      tap(m => {
        if (cambios.respuesta_leida) this.respuestasSinLeer.update(l => l.filter(x => x.id !== m.id));
      }),
    );
  }
}
